import { Component, signal, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ExamenesService, Examen, Pregunta, Intento, RespuestaIntento } from '../services/examenes.service';
import { NotificationService } from '../services/notification.service';
import { interval, Subscription } from 'rxjs';

@Component({
  selector: 'app-exam-test',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './exam-test.html',
  styleUrl: './exam-test.css',
})
export class ExamTestComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private examenesService = inject(ExamenesService);
  private notificationService = inject(NotificationService);

  // Estado de carga
  isLoading = signal(false);
  
  // Datos del examen
  examen = signal<Examen | null>(null);
  intento = signal<Intento | null>(null);
  
  // Respuestas
  respuestas = new Map<number, RespuestaIntento>();
  
  // Temporizador
  tiempoTranscurrido = signal<number>(0);
  private timerSubscription?: Subscription;
  
  // Estados
  examenIniciado = signal(false);
  examenFinalizado = signal(false);
  mostrandoResultados = signal(false);
  resultados = signal<any>(null);
  
  // Curso fijo
  private readonly CURSO_FIJO = 1;
  private examId: number | null = null;

  ngOnInit() {
    this.route.params.subscribe(params => {
      this.examId = +params['id'];
      
      if (this.examId) {
        this.cargarExamen(this.examId);
      }
    });
  }

  ngOnDestroy() {
    this.detenerTemporizador();
  }

  /**
   * Carga los datos del examen
   */
  cargarExamen(idExamen: number) {
    this.isLoading.set(true);
    
    this.examenesService.getExamen(idExamen).subscribe({
      next: (response) => {
        this.isLoading.set(false);
        
        if (response.ok && response.data) {
          const examenRecibido = response.data;
          
          // Eliminar duplicados de preguntas
          if (examenRecibido.preguntas && examenRecibido.preguntas.length > 0) {
            const resultado = this.analizarYLimpiarDuplicados(examenRecibido.preguntas);
            
            if (resultado.duplicadosEncontrados > 0) {
              console.log(`🧹 Se eliminaron ${resultado.duplicadosEncontrados} pregunta(s) duplicada(s)`);
              this.notificationService.success(
                'Preguntas validadas',
                `${resultado.unicos} preguntas únicas encontradas`
              );
            }
            
            examenRecibido.preguntas = resultado.preguntasLimpias;
          }
          
          this.examen.set(examenRecibido);
          this.notificationService.success(
            'Examen cargado',
            `${examenRecibido.preguntas.length} preguntas encontradas`
          );
        } else {
          this.notificationService.error('Error', 'No se pudo cargar el examen');
          this.goBack();
        }
      },
      error: (error) => {
        this.isLoading.set(false);
        this.notificationService.error('Error', 'Error al cargar el examen');
        this.goBack();
      }
    });
  }

  /**
   * Analiza y limpia preguntas duplicadas
   */
  private analizarYLimpiarDuplicados(preguntas: Pregunta[]): {
    preguntasLimpias: Pregunta[];
    total: number;
    unicos: number;
    duplicadosEncontrados: number;
    idsDuplicados: number[];
    detalles: any[];
  } {
    if (!preguntas || preguntas.length === 0) {
      return {
        preguntasLimpias: [],
        total: 0,
        unicos: 0,
        duplicadosEncontrados: 0,
        idsDuplicados: [],
        detalles: []
      };
    }
    
    const idsVistos = new Set<number>();
    const preguntasLimpias: Pregunta[] = [];
    const duplicadosInfo: any[] = [];
    const idsDuplicados: number[] = [];
    
    preguntas.forEach((pregunta, indice) => {
      if (!idsVistos.has(pregunta.id_pregunta)) {
        idsVistos.add(pregunta.id_pregunta);
        preguntasLimpias.push(pregunta);
      } else {
        idsDuplicados.push(pregunta.id_pregunta);
        duplicadosInfo.push({
          id: pregunta.id_pregunta,
          indice: indice,
          texto: pregunta.texto_pregunta.substring(0, 80) + '...'
        });
      }
    });
    
    return {
      preguntasLimpias,
      total: preguntas.length,
      unicos: preguntasLimpias.length,
      duplicadosEncontrados: duplicadosInfo.length,
      idsDuplicados: [...new Set(idsDuplicados)],
      detalles: duplicadosInfo
    };
  }

  /**
   * Inicia el examen
   */
  iniciarExamen() {
    const examenData = this.examen();
    if (!examenData) return;
    
    this.isLoading.set(true);
    
    this.examenesService.iniciarIntento(examenData.id_examen).subscribe({
      next: (response) => {
        this.isLoading.set(false);
        
        if (response.ok && response.data) {
          const data = response.data as any;
          
          // Guardar el intento correctamente
          if (data.intento) {
            this.intento.set(data.intento);
            console.log('✅ Intento iniciado con ID:', data.intento.id_intento);
            
            // Cargar preguntas del intento si vienen
            if (data.preguntas && data.preguntas.length > 0) {
              const resultado = this.analizarYLimpiarDuplicados(data.preguntas);
              
              if (resultado.duplicadosEncontrados > 0) {
                console.log(`🧹 Limpiados ${resultado.duplicadosEncontrados} duplicados`);
              }
              
              // Actualizar el examen con las preguntas limpias
              this.examen.set({
                ...examenData,
                preguntas: resultado.preguntasLimpias
              });
            }
            
            this.examenIniciado.set(true);
            this.iniciarTemporizador();
            this.notificationService.success('Examen iniciado', 'Puedes comenzar a responder');
          } else {
            this.notificationService.error('Error', 'No se pudo iniciar el examen');
          }
        } else {
          this.notificationService.error('Error', 'No se pudo iniciar el examen');
        }
      },
      error: (error) => {
        this.isLoading.set(false);
        this.notificationService.error('Error', 'Error al iniciar el examen');
      }
    });
  }

  /**
   * Inicia el temporizador
   */
  iniciarTemporizador() {
    this.timerSubscription = interval(1000).subscribe(() => {
      this.tiempoTranscurrido.set(this.tiempoTranscurrido() + 1);
    });
  }

  /**
   * Detiene el temporizador
   */
  detenerTemporizador() {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
    }
  }

  /**
   * Guarda una respuesta
   */
  guardarRespuesta(pregunta: Pregunta, idOpcion?: number, textoRespuesta?: string) {
    const intentoActual = this.intento();
    
    if (!intentoActual) {
      console.error('❌ No hay intento activo para guardar respuesta');
      return;
    }
    
    if (!intentoActual.id_intento) {
      console.error('❌ El intento no tiene ID válido');
      return;
    }

    let respuesta: RespuestaIntento;

    if (pregunta.tipo_pregunta === 'respuesta_corta' || pregunta.tipo_pregunta === 'respuesta_larga') {
      respuesta = {
        id_pregunta: pregunta.id_pregunta,
        respuesta_texto: textoRespuesta || ''
      };
    } else {
      respuesta = {
        id_pregunta: pregunta.id_pregunta,
        id_opcion: idOpcion
      };
    }
    
    // Guardar en el mapa local
    this.respuestas.set(pregunta.id_pregunta, respuesta);
    
    // Guardar en el backend
    this.examenesService.responderPregunta(intentoActual.id_intento, respuesta).subscribe({
      next: (response: any) => {
        console.log('✅ Respuesta guardada para pregunta:', pregunta.id_pregunta);
      },
      error: (error: any) => {
        console.error('❌ Error al guardar respuesta:', error);
      }
    });
  }

  /**
   * Obtiene la respuesta de una pregunta
   */
  getRespuesta(idPregunta: number): RespuestaIntento | undefined {
    return this.respuestas.get(idPregunta);
  }

  /**
   * Cuenta cuántas preguntas han sido respondidas
   */
  getPreguntasRespondidas(): number {
    return this.respuestas.size;
  }

  /**
   * Finaliza el examen
   */
  finalizarExamen() {
    const intentoActual = this.intento();
    
    if (!intentoActual) {
      console.error('❌ No hay intento activo para finalizar');
      this.notificationService.error('Error', 'No se puede finalizar: intento no encontrado');
      return;
    }
    
    if (!intentoActual.id_intento) {
      console.error('❌ El intento no tiene ID válido:', intentoActual);
      this.notificationService.error('Error', 'No se puede finalizar: ID de intento inválido');
      return;
    }
    
    console.log('🏁 Finalizando intento con ID:', intentoActual.id_intento);
    
    this.detenerTemporizador();
    this.isLoading.set(true);
    
    this.examenesService.finalizarIntento(intentoActual.id_intento).subscribe({
      next: (response: any) => {
        this.isLoading.set(false);
        
        if (response.ok && response.data) {
          this.examenFinalizado.set(true);
          
          // Guardar solo los resultados (sin las respuestas detalladas)
          if (response.data.resultados) {
            this.resultados.set(response.data.resultados);
            this.mostrandoResultados.set(true);
            
            console.log('📊 Resultados:', response.data.resultados);
            
            this.notificationService.success(
              'Examen finalizado', 
              `Puntuación: ${response.data.resultados.porcentaje}%`
            );
          } else {
            this.notificationService.success('Examen finalizado', 'Tu examen ha sido calificado');
          }
        } else {
          this.notificationService.error('Error', 'No se pudo finalizar el examen');
        }
      },
      error: (error: any) => {
        this.isLoading.set(false);
        console.error('❌ Error al finalizar:', error);
        this.notificationService.error('Error', 'Error al finalizar el examen');
      }
    });
  }

  /**
   * Formatea el tiempo en formato HH:MM:SS
   */
  formatTime(segundos: number): string {
    const horas = Math.floor(segundos / 3600);
    const minutos = Math.floor((segundos % 3600) / 60);
    const segs = segundos % 60;
    
    if (horas > 0) {
      return `${horas.toString().padStart(2, '0')}:${minutos.toString().padStart(2, '0')}:${segs.toString().padStart(2, '0')}`;
    }
    return `${minutos.toString().padStart(2, '0')}:${segs.toString().padStart(2, '0')}`;
  }

  /**
   * Vuelve al curso
   */
  goBack() {
    if (this.CURSO_FIJO) {
      this.router.navigate(['/course', this.CURSO_FIJO]);
    } else {
      this.router.navigate(['/dashboard/home']);
    }
  }
}
